import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { formatAddress, formatAmount, formatDate } from '../../utils';
import { IDepositorResponse } from '../../utils/types';
import { apiServices } from '../../utils/apiServices';
import { useTheme } from '../../context/ThemeContext';
import { ChevronLeftIcon, ChevronRightIcon, CopyIcon } from '../SVGIcons';
import LinearBorderContainer from '../LinearBorderContainer';
const DepositorListTable: React.FC<{ mode?: 'detailed' | 'top'; limit?: number }> = ({
  mode = 'detailed',
  limit = 5
}) => {
  const { poolBalances, addDepositorBalance } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof IDepositorResponse>('available_amount');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const navigate = useNavigate();
  const [depositors, setDepositors] = useState<IDepositorResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const fetchDepositors = async () => {
      setLoading(true);
      setError(null);
      try {
        const res: IDepositorResponse[] = await apiServices.depositors.getAll();
        setDepositors(res);
      } catch (err) {
        console.error('Failed to fetch depositors:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch depositors');
        setDepositors([]); // Reset depositors to empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchDepositors();
  }, []);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleSort = (field: keyof IDepositorResponse) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon: React.FC<{ field: keyof IDepositorResponse }> = ({ field }) => {
    if (sortField !== field) return null;
    return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const filteredAndSortedDepositors = useMemo(() => {
    let filtered = depositors;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(depositor =>
        depositor.address.toLowerCase().includes(searchLower)
      );
    }

    // Create a new array to avoid mutating the original
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Handle date fields
      if (sortField === 'updated_at' || sortField === 'created_at') {
        const dateA = aValue ? new Date(aValue as string).getTime() : 0;
        const dateB = bValue ? new Date(bValue as string).getTime() : 0;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Handle numeric fields that come as strings
      if (sortField === 'available_amount' || sortField === 'deposit_percent') {
        const numA = parseFloat(String(aValue)) || 0;
        const numB = parseFloat(String(bValue)) || 0;
        return sortDirection === 'asc'
          ? numA - numB
          : numB - numA;
      }

      // Handle string fields (like address)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [depositors, searchTerm, sortField, sortDirection]);

  const paginatedDepositors = useMemo(() => {
    const result = mode === 'top'
      ? filteredAndSortedDepositors.slice(0, limit)
      : filteredAndSortedDepositors.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return result;
  }, [filteredAndSortedDepositors, currentPage, itemsPerPage, mode, limit, sortField]);

  const totalPages = Math.ceil(filteredAndSortedDepositors.length / itemsPerPage);
  const thClsName = `px-6 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider font-borna  ${mode === 'detailed' ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''}`
  return (
    <LinearBorderContainer className="relative transition-colors duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 sm:px-6 py-4 space-y-4 sm:space-y-0">
        <h2 className="text-xl text-gray-800 dark:text-gray-200">
          {mode === 'top' ? 'Top Depositors' : 'Depositors'}
        </h2>
        {error && (
          <div className="text-red-600 text-sm">
            {error}
          </div>
        )}

        {mode === 'detailed' && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search depositors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pl-10 pr-4 py-0 w-full sm:w-auto"
              />
            </div>
            <div className="relative w-full sm:w-auto">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={`input-field text-black dark:text-white bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pr-10 pl-4 py-0 appearance-none w-full sm:w-auto ${theme === 'dark' ? 'dark' : ''}`}
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden px-4 sm:px-6 pb-4">
        {loading ? (
          <div className="text-gray-600 text-sm flex justify-center items-center h-[281px]">
            Loading...
          </div>
        ) : paginatedDepositors.map((depositor) => (
          <div
            key={depositor.address}
            onClick={() => navigate(`/depositor/${depositor.address}`)}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
                    {formatAddress(depositor.address)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyAddress(depositor.address);
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                    title="Copy address"
                  >
                    {copiedAddress === depositor.address ? (
                      <span className="text-green-600 block h-[17px] w-[16px]">✓</span>
                    ) : (
                      <CopyIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Available:</span>
                    <span className="ml-1 text-gray-900 dark:text-gray-200">{formatAmount(depositor.available_amount || "0")}τ</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Percent:</span>
                    <span className="ml-1 text-gray-900 dark:text-gray-200">{depositor.deposit_percent ? formatAmount(depositor.deposit_percent) : "0"}%</span>
                  </div>
                  {mode === 'detailed' && (
                    <>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Last Update:</span>
                        <span className="ml-1 text-gray-900 dark:text-gray-200">{formatDate(depositor?.updated_at || depositor?.created_at)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-gray-600 text-sm flex justify-center items-center h-[281px]">
              Loading...
            </div>
          ) : <table className="min-w-full divide-y divide-[rgba(0,0,0,0.06)] dark:divide-white/20">
            <thead className="">
              <tr>
                <th
                  className={thClsName}
                  onClick={() => handleSort('address')}
                >
                  Address {mode === 'detailed' && <SortIcon field="address" />}
                </th>
                <th
                  className={thClsName}
                  onClick={() => handleSort('available_amount')}
                >
                  Available Amount {mode === 'detailed' && <SortIcon field="available_amount" />}
                </th>
                <th
                  className={thClsName}
                  onClick={() => handleSort('deposit_percent')}
                >
                  Deposit Percent {mode === 'detailed' && <SortIcon field="deposit_percent" />}
                </th>
                {mode === 'detailed' && (
                  <th
                    className={thClsName}
                    onClick={() => handleSort('updated_at')}
                  >
                    Last Update <SortIcon field="updated_at" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="">
              {paginatedDepositors.map((depositor) => (
                <tr
                  key={depositor.address}
                  onClick={() => navigate(`/depositor/${depositor.address}`)}
                  className="cursor-pointer hover:bg-[#F2F2F2] dark:hover:bg-[#262626]"
                >
                  <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200 !border-0">
                    <div className="flex items-center space-x-2">
                      <span
                        className="group relative"
                        title={depositor.address}
                      >
                        {formatAddress(depositor.address)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyAddress(depositor.address);
                        }}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                        title="Copy address"
                      >
                        {copiedAddress === depositor.address ? (
                          <span className="text-green-600 block h-[17px] w-[16px]">✓</span>
                        ) : (
                          <CopyIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatAmount(depositor.available_amount || "0")}τ</td>
                  <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{depositor.deposit_percent ? formatAmount(depositor.deposit_percent) : "0"}%</td>
                  {mode === 'detailed' && (
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatDate(depositor?.updated_at || depositor?.created_at)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>}

        </div>
      </div>

      {mode === 'detailed' && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 px-4 sm:px-6 space-y-3 sm:space-y-0">
          <div className="text-sm text-black/50 dark:text-white/50">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedDepositors.length)} of {filteredAndSortedDepositors.length} depositors
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
            >
              <ChevronLeftIcon fillColor={currentPage === 1 ? 'gray' : 'white'} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
            >
              <ChevronRightIcon fillColor={currentPage === totalPages ? 'gray' : 'white'} />
            </button>
          </div>
        </div>
      )}
    </LinearBorderContainer>
  );
};

export default DepositorListTable;